/**
 * Anwesenheit (/anwesenheit): zwei Tabs —
 * Board (G-10…G-15): Datums-Kopf mit Schnellwahl, vier einklappbare Panels
 * aus EINEM board-Response und die Aktionsleiste (Einbuchen/Ausbuchen/
 * Abwesenheit melden/Umbuchen); Gate board_nutzen.
 * Reports (G-16): Zeitraum-/Zulagenreport mit XLSX-Export; Gate leitung_lesen.
 * Entscheidung: Reports als Tab hier statt eigener Route — ein Einstiegspunkt
 * fuer alles rund um Anwesenheitsbuchungen.
 */

import { useMemo, useState, type ReactNode } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { CheckCheck, ChevronDown, LogIn, LogOut, Repeat, UserX } from "lucide-react"
import { CheckInSheet } from "@/components/attendance/CheckInSheet"
import { CheckOutSheet } from "@/components/attendance/CheckOutSheet"
import { ATTENDANCE_STATUS } from "@/components/attendance/labels"
import { ReportAbsenceDialog } from "@/components/attendance/ReportAbsenceDialog"
import { RebookSheet } from "@/components/planning/RebookSheet"
import { AttendanceReportsTab } from "@/components/reports/AttendanceReportsTab"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ABSENCE_REASONS } from "@/lib/absenceColors"
import { fmtDate, fmtEuro, fmtHours } from "@/lib/format"
import { addDaysIso, todayIso } from "@/lib/isoWeek"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api } from "@/services/api"
import type { AttendanceRecord, BoardAbsenceRow } from "@/types"

// ---------------------------------------------------------------------------
// Einklappbares Board-Panel
// ---------------------------------------------------------------------------

function BoardSection({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string
  count: number
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 font-medium">
          {title}
          <Badge variant="secondary">{count}</Badge>
        </span>
        <ChevronDown
          className={cn("size-4 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  )
}

/** Σ der eingefrorenen Zulagen-Betraege einer Buchung */
function surchargeSum(record: AttendanceRecord): number {
  return record.surcharges.reduce((sum, s) => sum + Number(s.amount), 0)
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

function BoardTab() {
  const canBoard = useCan("board_nutzen")
  const canWrite = useCan("personal_schreiben")
  const today = useMemo(todayIso, [])

  const [date, setDate] = useState(today)
  const [checkInOpen, setCheckInOpen] = useState(false)
  const [checkOutOpen, setCheckOutOpen] = useState(false)
  const [checkOutPreselect, setCheckOutPreselect] = useState<string | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [rebookOpen, setRebookOpen] = useState(false)

  const board = useQuery({
    queryKey: ["attendance", "board", date],
    queryFn: () => api.attendance.board(date),
    enabled: canBoard && date !== "",
    placeholderData: keepPreviousData,
  })

  function openCheckOut(preselectId: string | null) {
    setCheckOutPreselect(preselectId)
    setCheckOutOpen(true)
  }

  // Gemeinsame Spalten der Buchungs-Listen (header als String fuer die Spalten-Auswahl)
  const recordColumns = useMemo<ColumnDef<AttendanceRecord>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (r) => r.employeeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "shift",
        header: "Schicht",
        accessorFn: (r) => r.shiftName ?? "",
        cell: ({ row }) => row.original.shiftName ?? "—",
      },
      {
        id: "position",
        header: "Stelle",
        accessorFn: (r) => r.positionName,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => ATTENDANCE_STATUS[r.status].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={ATTENDANCE_STATUS[row.original.status].badge}>
            {ATTENDANCE_STATUS[row.original.status].label}
          </Badge>
        ),
      },
    ],
    [],
  )

  // Aktions-Spalte "Ausbuchen" (nicht sortier-/ausblendbar)
  const checkOutActionColumn = useMemo<ColumnDef<AttendanceRecord>>(
    () => ({
      id: "actions",
      header: "",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => openCheckOut(row.original.id)}>
          <LogOut /> Ausbuchen
        </Button>
      ),
      meta: { headClassName: "w-28", cellClassName: "text-right" },
    }),
    // openCheckOut nutzt nur stabile State-Setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // 1) Eingebucht
  const checkedInColumns = useMemo<ColumnDef<AttendanceRecord>[]>(
    () => [
      recordColumns[0],
      recordColumns[1],
      recordColumns[2],
      {
        id: "workplace",
        header: "Arbeitsplatz",
        accessorFn: (r) => r.workplaceName ?? "",
        cell: ({ row }) => row.original.workplaceName ?? "—",
      },
      recordColumns[3],
      {
        id: "info",
        header: "Hinweis",
        accessorFn: (r) => r.info ?? "",
        meta: { cellClassName: "text-muted-foreground" },
      },
      checkOutActionColumn,
    ],
    [recordColumns, checkOutActionColumn],
  )

  // 2) Nicht ausgebucht (Vortag)
  const previousDayColumns = useMemo<ColumnDef<AttendanceRecord>[]>(
    () => [
      recordColumns[0],
      {
        id: "date",
        // ISO-String als Accessor sortiert korrekt
        header: "Datum",
        accessorFn: (r) => r.date,
        cell: ({ row }) => fmtDate(row.original.date),
        meta: { cellClassName: "tabular-nums" },
      },
      recordColumns[1],
      recordColumns[2],
      recordColumns[3],
      checkOutActionColumn,
    ],
    [recordColumns, checkOutActionColumn],
  )

  // 3) Abwesend (Stichtag)
  const absentColumns = useMemo<ColumnDef<BoardAbsenceRow>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (a) => a.employeeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "reason",
        header: "Grund",
        accessorFn: (a) => ABSENCE_REASONS[a.reason].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={ABSENCE_REASONS[row.original.reason].badge}>
            {ABSENCE_REASONS[row.original.reason].label}
          </Badge>
        ),
      },
      {
        id: "period",
        header: "Zeitraum",
        accessorFn: (a) => a.from,
        cell: ({ row }) => (
          <>
            {fmtDate(row.original.from)} – {fmtDate(row.original.to)}
          </>
        ),
        meta: { cellClassName: "tabular-nums" },
      },
    ],
    [],
  )

  // 4) Ausgebucht
  const checkedOutColumns = useMemo<ColumnDef<AttendanceRecord>[]>(
    () => [
      recordColumns[0],
      recordColumns[1],
      recordColumns[2],
      {
        id: "hours",
        header: "Stunden",
        accessorFn: (r) => (r.hours !== null ? Number(r.hours) : null),
        cell: ({ row }) =>
          row.original.hours !== null ? fmtHours(Number(row.original.hours)) : "—",
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => ATTENDANCE_STATUS[r.status].label,
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <Badge variant="outline" className={ATTENDANCE_STATUS[row.original.status].badge}>
              {ATTENDANCE_STATUS[row.original.status].label}
            </Badge>
            <CheckCheck className="size-4 text-muted-foreground" />
          </span>
        ),
      },
      {
        id: "surcharges",
        header: "Σ Zulagen",
        accessorFn: (r) => surchargeSum(r),
        cell: ({ row }) =>
          row.original.surcharges.length > 0 ? fmtEuro(surchargeSum(row.original)) : "—",
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
    ],
    [recordColumns],
  )

  if (!canBoard) {
    return (
      <p className="text-sm text-muted-foreground">
        Keine Berechtigung für das Anwesenheitsboard (board_nutzen).
      </p>
    )
  }

  const data = board.data
  const openRecords = data ? [...data.checkedIn, ...data.previousDayOpen] : []

  const quick: { label: string; value: string }[] = [
    { label: "Vorgestern", value: addDaysIso(today, -2) },
    { label: "Gestern", value: addDaysIso(today, -1) },
    { label: "Heute", value: today },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Aktionsleiste (Gate board_nutzen; Umbuchen zusätzlich personal_schreiben) */}
        <div className="flex flex-wrap gap-2">
          {canWrite && (
            <Button variant="outline" onClick={() => setRebookOpen(true)}>
              <Repeat /> Umbuchen
            </Button>
          )}
          <Button variant="outline" onClick={() => setReportOpen(true)}>
            <UserX /> Abwesenheit melden
          </Button>
          <Button variant="outline" onClick={() => openCheckOut(null)}>
            <LogOut /> Ausbuchen
          </Button>
          <Button onClick={() => setCheckInOpen(true)}>
            <LogIn /> Einbuchen
          </Button>
        </div>
      </div>

      {/* Datums-Kopf: Picker + Schnellwahl */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          className="w-44"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <div className="flex gap-1">
          {quick.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => setDate(q.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                date === q.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-transparent text-muted-foreground hover:bg-accent"
              )}
            >
              {q.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-muted-foreground">Stichtag: {fmtDate(date)}</span>
      </div>

      {board.isLoading || !data ? (
        <LoadingSpinner fullPage />
      ) : (
        <div className="space-y-3">
          {/* 1) Eingebucht */}
          <BoardSection title="Eingebucht" count={data.checkedIn.length}>
            <DataGrid
              columns={checkedInColumns}
              data={data.checkedIn}
              storageKey="attendance-checkedin"
              emptyMessage="Niemand eingebucht."
            />
          </BoardSection>

          {/* 2) Nicht ausgebucht (Vortag) */}
          <BoardSection
            title="Nicht ausgebucht (Vortag)"
            count={data.previousDayOpen.length}
            defaultOpen={data.previousDayOpen.length > 0}
          >
            <DataGrid
              columns={previousDayColumns}
              data={data.previousDayOpen}
              storageKey="attendance-previous-open"
              emptyMessage="Kein Vortags-Überhang."
            />
          </BoardSection>

          {/* 3) Abwesend (Stichtag) */}
          <BoardSection title="Abwesend" count={data.absent.length}>
            <DataGrid
              columns={absentColumns}
              data={data.absent}
              storageKey="attendance-absent"
              emptyMessage="Keine laufenden Abwesenheiten am Stichtag."
            />
          </BoardSection>

          {/* 4) Ausgebucht */}
          <BoardSection title="Ausgebucht" count={data.checkedOut.length}>
            <DataGrid
              columns={checkedOutColumns}
              data={data.checkedOut}
              storageKey="attendance-checkedout"
              emptyMessage="Noch niemand ausgebucht."
            />
          </BoardSection>
        </div>
      )}

      <CheckInSheet open={checkInOpen} onOpenChange={setCheckInOpen} date={date} />
      <CheckOutSheet
        open={checkOutOpen}
        onOpenChange={setCheckOutOpen}
        records={openRecords}
        preselectedId={checkOutPreselect}
      />
      <ReportAbsenceDialog open={reportOpen} onOpenChange={setReportOpen} date={date} />
      <RebookSheet open={rebookOpen} onOpenChange={setRebookOpen} date={date} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite: Tabs Board (board_nutzen) + Reports (leitung_lesen)
// ---------------------------------------------------------------------------

export function AttendancePage() {
  const canBoard = useCan("board_nutzen")
  const canReports = useCan("leitung_lesen")
  const [tab, setTab] = useState(canBoard ? "board" : "reports")

  if (!canBoard && !canReports) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Anwesenheit</h1>
        <p className="text-sm text-muted-foreground">
          Keine Berechtigung für Anwesenheitsboard oder Reports.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Anwesenheit</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {canBoard && <TabsTrigger value="board">Board</TabsTrigger>}
          {canReports && <TabsTrigger value="reports">Reports</TabsTrigger>}
        </TabsList>
        {canBoard && (
          <TabsContent value="board">
            <BoardTab />
          </TabsContent>
        )}
        {canReports && (
          <TabsContent value="reports">
            <AttendanceReportsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
